import React, { useState } from "react";
import { Button, Grid, TextField, Snackbar, Alert } from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { changePassword } from "redux/actions";

interface ChangePasswordFormProps {
  onClose: () => void;
  userId: number;
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ onClose, userId }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const initialValues = {
    newPassword: "",
    confirmPassword: "",
  };

  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("New password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], "Passwords must match")
      .required("Confirm password is required"),
  });

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      const response = await changePassword(userId, values.newPassword);
      if (
        response.data?.status === "Updated" ||
        response.data?.status === "success"
      ) {
        setSnackbarOpen(true); 
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        console.warn("Password change failed:", response.data?.message);
      }
    } catch (error: any) {
      console.error("Change password error:", error);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ touched, errors }) => (
          <Form>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  name="newPassword"
                  label="New Password"
                  type="password"
                  fullWidth
                  error={touched.newPassword && !!errors.newPassword}
                  helperText={<ErrorMessage name="newPassword" />}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  fullWidth
                  error={touched.confirmPassword && !!errors.confirmPassword}
                  helperText={<ErrorMessage name="confirmPassword" />}
                />
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={onClose} variant="outlined">
                  Cancel
                </Button>
                <Button type="submit" variant="contained">
                  Save Changes
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Password changed successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChangePasswordForm;
